import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Field } from './blocks';
import { DataError } from './DataError';
import { useApi } from '../lib/useApi';

export interface FilterRecord { id: string; label: string; detail?: string; kind?: string }

/** Find a record by its readable name, without requiring the operator to copy its ID. */
export function RecordFilter({ label, placeholder, value, selectedLabel, search, onPick }: {
  label: string;
  placeholder: string;
  value: string;
  selectedLabel: string;
  search: (query: string, signal: AbortSignal) => Promise<FilterRecord[]>;
  onPick: (item: FilterRecord | null) => void;
}) {
  const { t } = useTranslation();
  const { t: ta } = useTranslation('accounts');
  const { t: tn } = useTranslation('nav');
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(draft.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [draft]);
  const results = useApi((signal) => query.length >= 2 && !value ? search(query, signal) : Promise.resolve([]), [query, value]);
  return (
    <Field label={label} group className="relative w-full sm:w-72">
      {value ? (
        <Button type="button" variant="secondary" size="sm" className="w-full justify-between" aria-label={ta('filter.clearFilter', { label: selectedLabel })} onClick={() => { setDraft(''); onPick(null); }}>
          <span className="truncate">{selectedLabel}</span><X className="size-4 shrink-0" />
        </Button>
      ) : (
        <div onFocusCapture={() => setOpen(true)} onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}>
          <Input type="search" aria-label={label} placeholder={placeholder} value={draft} onChange={(e) => { setDraft(e.target.value); setOpen(true); }} onKeyDown={(e) => { if (e.key === 'Escape') { setOpen(false); e.stopPropagation(); } }} autoComplete="off" spellCheck={false} className="h-8" />
          {open && draft.trim().length >= 2 ? (
            <div className="absolute top-full z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md" aria-live="polite">
              {query !== draft.trim() || results.status === 'loading' ? <p className="p-2 text-xs text-muted-foreground">{tn('ui.searching')}</p> : null}
              {results.status === 'error' ? <DataError error={results.error} onRetry={results.reload} /> : null}
              {query === draft.trim() && results.status === 'success' ? results.data.length ? results.data.map((item) => (
                <button key={`${item.kind ?? ''}/${item.id}`} type="button" className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-none" onClick={() => onPick(item)}>
                  <span className="block font-medium">{item.label}</span>
                  {item.detail ? <span className="block text-xs text-muted-foreground">{item.detail}</span> : null}
                </button>
              )) : <p className="p-2 text-xs text-muted-foreground">{t('table.noResults')}</p> : null}
            </div>
          ) : null}
        </div>
      )}
    </Field>
  );
}
